<template>
  <div>
    <transition name="modal">
      <delete-modal
        v-if="showDeleteModal"
        :loading="loading"
        :message="'¿Estás seguro que deseas rechazar la solicitud?'"
        :buttonLabel="'Confirmar'"
        @close="closeDeleteModal()"
        @confirmDelete="rejectRequest"
      ></delete-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="showConfirmationModal"
        :message="getConfirmationMessage()"
        @close="showConfirmationModal = false"
        @confirm="approveRequest"
      />
    </transition>

    <div v-if="!loading">
      <div v-if="orderRequest" class="flex flex-col">
        <div class="card w-full">
          <card-header :title="'Solicitud #' + orderRequest.id">
            <template #badge>
              <status-badge :status="orderRequest.status" :label="getStatus(orderRequest.status)" />
            </template>
          </card-header>

          <section-header title="Información General" />

          <div class="detail-grid">
            <detail-field label="Cliente">
              <router-link
                :to="{
                  name: 'clientsDetail',
                  params: { id: orderRequest.client.id },
                }"
                class="detail-link"
                >{{ orderRequest.client.name }}
                {{ orderRequest.client.last_name }}</router-link
              >
            </detail-field>

            <detail-field label="Número de semanas por orden" :value="orderRequest.order_cycle.cycle_duration" />
            <detail-field label="Fecha de creación" :value="formatDatetime(orderRequest.created_at)" />
            <detail-field label="Fecha de última actualización" :value="formatDatetime(orderRequest.updated_at)" />
            <detail-field label="Cantidad de órdenes solicitadas" :value="orderRequest.requested_orders_count" :prominent="true" />
          </div>

          <div v-if="orderRequest.requires_additional_payment" class="mt-4">
            <detail-field label="Pago adicional requerido">
              <p class="text-lg font-semibold text-warning-600">
                {{ integerToCurrency(orderRequest.additional_payment_amount) }}
              </p>
              <p class="text-xs text-warning-600 mt-1">
                Este monto será cobrado automáticamente al aceptar la solicitud
              </p>
            </detail-field>
          </div>

          <div v-if="orderRequest.requires_additional_payment" class="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p class="text-sm text-yellow-800">
              <strong>⚠️ Atención:</strong> Esta solicitud requiere un pago adicional que se cobrará automáticamente al cliente cuando apruebes la solicitud.
            </p>
          </div>

          <p class="font-medium mt-4">Dietas por mascota</p>

          <div
            v-for="diet in orderRequest.order_cycle.quote.diets.filter(
              (diet) => diet.status === 'active'
            )"
            :key="diet.id"
            class="mt-2"
          >
            <router-link
              :to="{
                name: 'petsDetail',
                params: { id: diet.pet.id },
              }"
              class="underline system-button"
              >{{ diet.pet.name }}
            </router-link>

            <!-- <diet-detail
              :diet="diet"
              :weeks="orderRequest.order_cycle.cycle_duration"
            /> -->

            <diet :diet="diet" />
          </div>

          <!-- <div class="mt-2 text-sm">
            <p class="font-medium">Mascotas incluídas</p>
            <div class="flex flew-wrap">
              <div v-for="pet in orderRequest.pets" :key="pet.id">
                <router-link
                  :to="{
                    name: 'petsDetail',
                    params: { id: pet.id },
                  }"
                  class="underline mr-2"
                  >{{ pet.name }}</router-link
                >
              </div>
            </div>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Comentarios</p>
            <p>{{ orderRequest.comments }}</p>
          </div> -->

          <primary-button
            v-if="
              orderRequest.status === 'pending' &&
              hasPermission('edit_order_requests')
            "
            class="mt-4 mx-0"
            @buttonClicked="showConfirmationModal = true"
          >
            Aceptar Solicitud
          </primary-button>

          <button
            v-if="
              orderRequest.status === 'pending' &&
              hasPermission('edit_order_requests')
            "
            class="mt-6 delete-button"
            @click="openDeleteModal()"
          >
            Rechazar
          </button>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import Diet from "@/components/models/Diet";
import api from "@/api";
import deleteMixin from "@/mixins/deleteMixin";
import { formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Order Request Detail",

  components: {
    ConfirmationModal,
    Diet,
  },

  data() {
    return {
      orderRequest: null,
      loading: false,
      showConfirmationModal: false,
    };
  },

  mixins: [deleteMixin],

  created() {
    this.$store.commit("setSelectedTab", "orderRequests");

    this.loadOrderRequest();
  },

  methods: {
    formatDatetime,

    integerToCurrency,

    getStatus(status) {
      switch (status) {
        case "pending":
          return "Pendiente";
        case "completed":
          return "Completada";
        case "rejected":
          return "Rechazada";
        default:
          return "";
      }
    },

    getBackgroundColor() {
      switch (this.orderRequest.status) {
        case "pending":
          return "bg-inProcess";
        case "completed":
          return "bg-ready";
        case "rejected":
          return "bg-failed";
        default:
          return "";
      }
    },

    async loadOrderRequest() {
      this.loading = true;

      try {
        this.orderRequest = await api.orderRequests.getById(
          this.$route.params.id
        );

        this.orderRequest.order_cycle.quote.diets.forEach((diet) => {
          diet.collapsed = true;
        });
        this.$store.commit('setBreadcrumbLabel', {
          routeName: this.$route.name,
          label: 'Solicitud #' + this.orderRequest.id,
        });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: "orderRequestsIndex" });
      }

      this.loading = false;
    },

    async approveRequest() {
      this.showConfirmationModal = false;
      this.loading = true;

      try {
        const response = await api.orderRequests.approve(this.orderRequest.id);
        this.$store.dispatch("successSnack", response.message);
        this.$router.push({ name: "orderRequestsIndex" });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async rejectRequest() {
      this.closeDeleteModal();
      this.loading = true;

      try {
        const response = await api.orderRequests.reject(this.orderRequest.id);
        this.$store.dispatch("successSnack", response.message);
        this.$router.push({ name: "orderRequestsIndex" });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    getConfirmationMessage() {
      if (!this.orderRequest) return "¿Estás seguro que deseas aceptar esta solicitud?";
      
      const count = this.orderRequest.requested_orders_count;
      const baseMessage = `¿Estás seguro que deseas aceptar la solicitud de ${count} ${count === 1 ? 'orden' : 'órdenes'}?`;
      
      if (this.orderRequest.requires_additional_payment) {
        return `${baseMessage}\n\nSe cobrará automáticamente ${this.integerToCurrency(this.orderRequest.additional_payment_amount)} al cliente.`;
      }
      
      return baseMessage;
    },
  },
};
</script>